import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { CheckIcon, AlertIcon, DocumentIcon, TrashIcon, RefreshIcon } from '../components/Icons';

const Settings = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    cnpj: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    description: '',
    dpoName: '',
    dpoEmail: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setFormData({
        companyName: response.data.companyName || '',
        cnpj: response.data.cnpj || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zipCode: response.data.zipCode || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        website: response.data.website || '',
        instagram: response.data.instagram || '',
        facebook: response.data.facebook || '',
        linkedin: response.data.linkedin || '',
        description: response.data.description || '',
        dpoName: response.data.dpoName || '',
        dpoEmail: response.data.dpoEmail || ''
      });
    } catch (err) {
      console.error(t('settings.a1'), err);
      setError(t('set.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put('/settings', formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(t('settings.a2'), err);
      setError(t('set.errSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Formatadores de campos
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .slice(0, 14);
    }
    return numbers
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  };

  const handleFormattedChange = (field, value, formatter) => {
    const formatted = formatter(value);
    setFormData(prev => ({ ...prev, [field]: formatted }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshIcon className="w-8 h-8 text-whatsapp animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('set.title')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal - Dados da Empresa */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informacoes da Empresa */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('set.company')}</h2>

            {saved && (
              <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-xl border border-green-200 flex items-center gap-2">
                <CheckIcon className="w-5 h-5" />
                <span>{t('set.saved')}</span>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 flex items-center gap-2">
                <AlertIcon className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.j1')}</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => handleFormattedChange('cnpj', e.target.value, formatCNPJ)}
                    placeholder="00.000.000/0001-00"
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereco
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder={t('set.addressPlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="SP"
                    maxLength={2}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleFormattedChange('zipCode', e.target.value, formatCEP)}
                    placeholder="00000-000"
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleFormattedChange('phone', e.target.value, formatPhone)}
                    placeholder="(00) 00000-0000"
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder={t('set.emailPlaceholder')}
                    className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder={t('set.websitePlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.j2')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  placeholder={t('set.descPlaceholder')}
                  className="w-full px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none resize-none"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20 disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshIcon className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckIcon className="w-5 h-5" />
                  )}
                  {saving ? t('settings.a3') : t('settings.a4')}
                </button>
              </div>
            </form>
          </div>

          {/* Redes Sociais */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('set.social')}</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram
                </label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder={t('set.instagramPlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facebook
                </label>
                <input
                  type="text"
                  value={formData.facebook}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  placeholder={t('set.facebookPlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn
                </label>
                <input
                  type="text"
                  value={formData.linkedin}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  placeholder={t('set.linkedinPlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>
            </div>
          </div>

          {/* LGPD / DPO */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('set.dpo')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('settings.j3')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.j4')}</label>
                <input
                  type="text"
                  value={formData.dpoName}
                  onChange={(e) => handleChange('dpoName', e.target.value)}
                  placeholder={t('set.fullName')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.j5')}</label>
                <input
                  type="email"
                  value={formData.dpoEmail}
                  onChange={(e) => handleChange('dpoEmail', e.target.value)}
                  placeholder={t('set.dpoEmailPlaceholder')}
                  className="w-full h-11 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Coluna Lateral - Perfil e Zona de Perigo */}
        <div className="space-y-6">
          {/* Idioma */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('set.language')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('set.languageHint')}</p>
            <LanguageSwitcher variante="completo" />
          </div>

          {/* Profile Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('set.profile')}</h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-whatsapp rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AD'}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{user?.name || 'Admin'}</h3>
                <p className="text-sm text-gray-600">{user?.email || t('settings.a5')}</p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-whatsapp/10 text-whatsapp text-xs font-medium rounded">
                  {user?.role === 'admin' ? t('users.admin') : t('users.operator')}
                </span>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('set.footerPreview')}</h2>
            <div className="bg-gray-900 rounded-xl p-4 text-white text-xs">
              <p className="font-semibold text-whatsapp mb-2">{formData.companyName || t('settings.a6')}</p>
              <p className="text-gray-400 mb-1">{formData.address || 'Endereco'}</p>
              <p className="text-gray-400 mb-1">{formData.city}{formData.state ? ` - ${formData.state}` : ''}</p>
              <p className="text-gray-400 mb-1">{formData.phone || 'Telefone'}</p>
              <p className="text-gray-400">{formData.email || 'Email'}</p>
              {formData.cnpj && (
                <p className="text-gray-500 mt-2 text-[10px]">CNPJ: {formData.cnpj}</p>
              )}
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertIcon className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-700">{t('set.dangerZone')}</h3>
              </div>
              <p className="text-xs text-red-600 mb-3">
                Acoes irreversiveis.
              </p>
              <div className="space-y-2">
                <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white text-red-600 border border-red-300 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors">
                  <DocumentIcon className="w-4 h-4" />{t('settings.j6')}</button>
                <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                  <TrashIcon className="w-4 h-4" />{t('settings.j7')}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
